/* Velven kin — finish similarity scoring for the PDP kin strip and the
   favourites / peek suggestion rows. Pure functions over VELVEN_MATERIALS
   records (see snippets/vmcm-materials-data.liquid for the record shape).
   The material keyword lists must stay identical to the Liquid derivation
   in snippets/vmcm-fin-tile.liquid. */
(function (root) {
  'use strict';

  var STONE = /stone|terrazzo|marble|concrete|cement/;
  var SOLID = /natural-white|cashmere-beige/;
  var TONES = { light: 0, mid: 1, dark: 2 };
  var SIZE_KEYS = ['a4', 'r60', 'r90', 'r120'];
  var MAX_DIST = Math.sqrt(3 * 255 * 255);

  function materialClass(handle) {
    var h = String(handle || '').toLowerCase();
    if (STONE.test(h)) return 'stone';
    if (SOLID.test(h)) return 'solid';
    return 'wood';
  }

  function toneIndex(tone) {
    var t = String(tone || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(TONES, t) ? TONES[t] : null;
  }

  function parseHex(hex) {
    if (typeof hex !== 'string') return null;
    var m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return null;
    var s = m[1];
    if (s.length === 3) s = s.charAt(0) + s.charAt(0) + s.charAt(1) + s.charAt(1) + s.charAt(2) + s.charAt(2);
    return [
      parseInt(s.slice(0, 2), 16),
      parseInt(s.slice(2, 4), 16),
      parseInt(s.slice(4, 6), 16),
    ];
  }

  function hexDist(a, b) {
    var ca = parseHex(a);
    var cb = parseHex(b);
    if (!ca || !cb) return null;
    var dr = ca[0] - cb[0];
    var dg = ca[1] - cb[1];
    var db = ca[2] - cb[2];
    return Math.sqrt(dr * dr + dg * dg + db * db) / MAX_DIST;
  }

  function purchasable(m) {
    if (!m) return false;
    var sizes = m.sizes || {};
    for (var i = 0; i < SIZE_KEYS.length; i++) {
      var s = sizes[SIZE_KEYS[i]];
      if (s && s.id && s.available) return true;
    }
    return !!m.available;
  }

  function score(current, cand) {
    var s = 0;
    if (materialClass(current && current.handle) === materialClass(cand && cand.handle)) s += 3;
    var ta = toneIndex(current && current.tone);
    var tb = toneIndex(cand && cand.tone);
    if (ta !== null && tb !== null) s += Math.max(0, 2 - Math.abs(ta - tb));
    var d = hexDist(current && current.ground, cand && cand.ground);
    if (d !== null) s += 2 * (1 - d);
    if (purchasable(cand)) s += 0.5;
    return s;
  }

  function cmpName(a, b) {
    var na = String((a && a.name) || '');
    var nb = String((b && b.name) || '');
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  }

  function orderByScore(scored) {
    scored.sort(function (a, b) {
      if (a.buy !== b.buy) return a.buy ? -1 : 1;
      if (b.s !== a.s) return b.s - a.s;
      return cmpName(a.m, b.m);
    });
    var out = [];
    for (var i = 0; i < scored.length; i++) out.push(scored[i].m);
    return out;
  }

  function rank(current, materials) {
    if (!current || !materials || !materials.length) return [];
    var scored = [];
    for (var i = 0; i < materials.length; i++) {
      var m = materials[i];
      if (!m || !m.handle || m.handle === current.handle) continue;
      scored.push({ m: m, s: score(current, m), buy: purchasable(m) });
    }
    return orderByScore(scored);
  }

  function rankToSet(saved, materials) {
    if (!materials || !materials.length) return [];
    var savedList = saved || [];
    var savedHandles = {};
    for (var i = 0; i < savedList.length; i++) {
      if (savedList[i] && savedList[i].handle) savedHandles[savedList[i].handle] = true;
    }
    var scored = [];
    for (var j = 0; j < materials.length; j++) {
      var m = materials[j];
      if (!m || !m.handle || savedHandles[m.handle]) continue;
      var best = 0;
      for (var k = 0; k < savedList.length; k++) {
        var sc = score(savedList[k], m);
        if (sc > best) best = sc;
      }
      scored.push({ m: m, s: best, buy: purchasable(m) });
    }
    return orderByScore(scored);
  }

  var api = {
    materialClass: materialClass,
    toneIndex: toneIndex,
    hexDist: hexDist,
    purchasable: purchasable,
    score: score,
    rank: rank,
    rankToSet: rankToSet,
  };

  root.VMCM_KIN = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  /* DOM hydration: re-order the server rendered kin strip by similarity.
     The strip never carries bare data-handle (that is a selection sheet
     trigger inside .vmcm-feed); tile handles are read from the hearts. */
  function hydrate() {
    var doc = root.document;
    if (!doc) return;
    var strip = doc.querySelector('[data-kin]');
    if (!strip) return;
    var mats = root.VELVEN_MATERIALS || [];
    if (mats.length < 2) return;
    var currentHandle = strip.getAttribute('data-kin-current');
    var current = null;
    for (var i = 0; i < mats.length; i++) {
      if (mats[i] && mats[i].handle === currentHandle) { current = mats[i]; break; }
    }
    if (!current) return;
    var ranked = rank(current, mats);
    var tiles = strip.querySelectorAll('.vmcm-fin');
    var byHandle = {};
    for (var j = 0; j < tiles.length; j++) {
      var heart = tiles[j].querySelector('[data-fav-handle]');
      if (heart) byHandle[heart.getAttribute('data-fav-handle')] = tiles[j];
    }
    for (var k = 0; k < ranked.length; k++) {
      var li = byHandle[ranked[k].handle];
      if (li) strip.appendChild(li);
    }
    var tail = strip.querySelector('.vmcm-kin__all');
    if (tail) strip.appendChild(tail);
    strip.scrollLeft = 0;
  }

  /* Desktop affordance: arrow buttons page the strip by roughly one viewport
     of tiles. Shown only when the strip actually overflows. */
  function initArrows(strip) {
    var doc = root.document;
    if (!doc) return;
    var nav = doc.querySelector('[data-kin-nav]');
    if (!nav) return;
    var prev = nav.querySelector('[data-kin-prev]');
    var next = nav.querySelector('[data-kin-next]');

    function step() {
      var tile = strip.querySelector('li');
      var gap = 18;
      var w = tile ? tile.getBoundingClientRect().width + gap : 260;
      return Math.max(w, Math.floor(strip.clientWidth * 0.8));
    }
    function sync() {
      nav.hidden = strip.scrollWidth <= strip.clientWidth + 4;
      if (nav.hidden) return;
      if (prev) prev.disabled = strip.scrollLeft <= 4;
      if (next) next.disabled = strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 4;
    }
    function go(dir) {
      // Direct assignment, not smooth scrollBy: a smooth scroll needs
      // rendering frames and can be cancelled mid flight; the x snap settles
      // the landing. Sync immediately too, scroll events only fire when the
      // tab is actually painting.
      strip.scrollLeft = strip.scrollLeft + dir * step();
      sync();
    }
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    strip.addEventListener('scroll', sync, { passive: true });
    root.addEventListener('resize', sync);
    sync();
  }

  function boot() {
    hydrate();
    var strip = root.document.querySelector('[data-kin]');
    if (strip) initArrows(strip);
  }

  if (root.document) {
    if (root.document.readyState !== 'loading') boot();
    else root.document.addEventListener('DOMContentLoaded', boot);
  }
})(typeof self !== 'undefined' ? self : this);
